import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { fotografarFormulario } from "@/shared/lib/fotografiaFormulario";

type Sujo = boolean | (() => boolean);

/**
 * Protege uma tela de Criar/Editar contra perda de dados: enquanto `sujo` for verdadeiro, qualquer
 * saída da tela (seta ‹ do cabeçalho, menu inferior, links, botão "Voltar", voltar do navegador)
 * fica pendente até a pessoa decidir, e fechar/atualizar a aba mostra o aviso do navegador.
 * `sujo` pode ser uma função, avaliada só no momento de sair.
 *
 * Uso: renderize `<ModalSairSemSalvar aberto={pedindoSaida} onSair={confirmarSaida}
 * onContinuar={continuarEditando} />` e chame `liberar()` logo ANTES de navegar depois de salvar.
 */
export function useProtecaoSaida(sujo: Sujo) {
  const sujoRef = useRef<Sujo>(sujo);
  const liberadoRef = useRef(false);
  sujoRef.current = sujo;

  const estaSujo = useCallback(() => {
    const v = sujoRef.current;
    return typeof v === "function" ? v() : v;
  }, []);

  const bloquear = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string; search: string };
      nextLocation: { pathname: string; search: string };
    }) =>
      !liberadoRef.current &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search) &&
      estaSujo(),
    [estaSujo],
  );
  const blocker = useBlocker(bloquear);

  useEffect(() => {
    function aoFechar(e: BeforeUnloadEvent) {
      if (liberadoRef.current || !estaSujo()) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", aoFechar);
    return () => window.removeEventListener("beforeunload", aoFechar);
  }, [estaSujo]);

  const liberar = useCallback(() => {
    liberadoRef.current = true;
  }, []);

  return {
    pedindoSaida: blocker.state === "blocked",
    confirmarSaida: () => blocker.proceed?.(),
    continuarEditando: () => blocker.reset?.(),
    liberar,
  };
}

/**
 * Para telas cujo formulário guarda os valores por conta própria (react-hook-form, campos não
 * controlados): compara os campos do `<form>` dentro da área marcada com como estavam no primeiro
 * toque/tecla da pessoa. Espalhe `areaProps` num elemento que envolva o formulário:
 * `<div {...areaProps}>…</div>`.
 */
export function useProtecaoFormulario() {
  const areaRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<string | null>(null);

  const marcarBase = useCallback(() => {
    if (baseRef.current === null) baseRef.current = fotografarFormulario(areaRef.current);
  }, []);

  const protecao = useProtecaoSaida(() => {
    const base = baseRef.current;
    return base !== null && fotografarFormulario(areaRef.current) !== base;
  });

  return {
    ...protecao,
    areaProps: {
      ref: areaRef,
      onPointerDownCapture: marcarBase,
      onKeyDownCapture: marcarBase,
      onFocusCapture: marcarBase,
    },
  };
}
